import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Funnel Dashboard — visualises scope-draw funnel conversion
 * (visitor → draft started → draft completed → checkout started → checkout completed).
 * Reads `/api/admin/analytics/funnel` for the selected date range and path filter.
 */
export function AdminFunnelDashboard({ token }) {
  const [days, setDays] = useState(7);
  const [path, setPath] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('days', days);
      if (path) params.set('path', path);
      const res = await axios.get(`${API_URL}/api/admin/analytics/funnel?${params.toString()}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load funnel');
    } finally {
      setLoading(false);
    }
  }, [days, path, headers]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  const stageLabels = {
    draft_started: 'Draft started',
    draft_completed: 'Draft completed',
    checkout_started: 'Checkout started',
    checkout_completed: 'Checkout completed',
  };

  const stages = data?.stages || {};
  const stageKeys = ['draft_started', 'draft_completed', 'checkout_started', 'checkout_completed'];
  const maxCount = Math.max(1, ...stageKeys.map((k) => stages[k] || 0));

  const stepRate = (from, to) => {
    const fromVal = stages[from] || 0;
    if (fromVal === 0) return null;
    return Math.round(((stages[to] || 0) / fromVal) * 1000) / 10;
  };

  const fmtMs = (ms) => {
    if (!ms) return '—';
    const h = ms / 3600000;
    if (h < 1) return `${Math.round(ms / 60000)}m`;
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };

  return (
    <div style={{ padding: 24 }} data-testid="admin-funnel-dashboard">
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Range:
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="funnel-range-select"
                  style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 6 }}>
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
        <label style={{ fontSize: 13, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Path:
          <select value={path} onChange={(e) => setPath(e.target.value)}
                  data-testid="funnel-path-select"
                  style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 6 }}>
            <option value="">All paths</option>
            <option value="draw">Drawing canvas</option>
            <option value="chat">Chat-style</option>
            <option value="upload">Upload floor plan</option>
          </select>
        </label>
        <button onClick={fetchFunnel} className="btn-secondary"
                data-testid="funnel-refresh"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13 }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !data && (
        <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
          Loading funnel data…
        </div>
      )}

      {data && (
        <>
          {/* Top-line KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))',
                        gap: 14, marginBottom: 22 }}>
            <div style={{ background: '#fff', padding: 18, border: '1px solid #E2E8F0', borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', color: '#64748B',
                            textTransform: 'uppercase' }}>Drafts started</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0F2742',
                            fontFamily: "'DM Serif Display',serif", marginTop: 4 }}
                   data-testid="kpi-drafts-started">{stages.draft_started ?? 0}</div>
            </div>
            <div style={{ background: '#fff', padding: 18, border: '1px solid #E2E8F0', borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', color: '#64748B',
                            textTransform: 'uppercase' }}>Completed checkouts</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0F2742',
                            fontFamily: "'DM Serif Display',serif", marginTop: 4 }}
                   data-testid="kpi-checkouts-completed">{stages.checkout_completed ?? 0}</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                          padding: 18, border: '1px solid #BFDBFE', borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', color: '#1D4ED8',
                            textTransform: 'uppercase' }}>End-to-end conversion</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0F2742',
                            fontFamily: "'DM Serif Display',serif", marginTop: 4 }}
                   data-testid="kpi-conversion-rate">
                {data.conversion_rate_end_to_end ?? 0}%
              </div>
            </div>
            <div style={{ background: '#fff', padding: 18, border: '1px solid #E2E8F0', borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em', color: '#64748B',
                            textTransform: 'uppercase' }}>Time to purchase (p50)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#0F2742',
                            fontFamily: "'DM Serif Display',serif", marginTop: 4 }}
                   data-testid="kpi-ttp">
                {fmtMs(data.time_to_purchase_ms?.median)}
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                n={data.time_to_purchase_ms?.samples ?? 0}
              </div>
            </div>
          </div>

          {/* Biggest drop-off highlight */}
          {data.biggest_drop_off && data.biggest_drop_off.drop > 0 && (
            <div style={{
              background: '#FEF3C7', border: '1.5px solid #FCD34D', borderRadius: 10,
              padding: 14, marginBottom: 18, display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
            }} data-testid="funnel-biggest-drop">
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em',
                              color: '#92400E', textTransform: 'uppercase', marginBottom: 2 }}>
                  Most common abandonment step
                </div>
                <div style={{ fontSize: 14, color: '#0F2742' }}>
                  <strong>{stageLabels[data.biggest_drop_off.from_stage]}</strong>
                  {' → '}
                  <strong>{stageLabels[data.biggest_drop_off.to_stage]}</strong>
                  {' · '}
                  <span style={{ color: '#92400E' }}>
                    {data.biggest_drop_off.drop} users lost ({data.biggest_drop_off.drop_pct}%)
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Funnel bars */}
          <div style={{ background: '#fff', padding: 20, border: '1px solid #E2E8F0', borderRadius: 10 }}
               data-testid="funnel-bars">
            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#0F2742', margin: '0 0 16px' }}>Funnel stages</h3>
            {stageKeys.map((key, i) => {
              const count = stages[key] || 0;
              const width = `${(count / maxCount) * 100}%`;
              const prevKey = i > 0 ? stageKeys[i - 1] : null;
              const rate = prevKey ? stepRate(prevKey, key) : null;
              return (
                <div key={key} style={{ marginBottom: 14 }} data-testid={`funnel-stage-${key}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13,
                                color: '#0F2742', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{stageLabels[key]}</span>
                    <span>
                      <strong>{count}</strong>
                      {rate != null && (
                        <span style={{ marginLeft: 10, color: rate >= 50 ? '#059669' : '#94A3B8', fontSize: 12 }}>
                          {rate >= 50 ? <TrendingUp size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />
                                       : <TrendingDown size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />}
                          {' '}{rate}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ height: 10, background: '#F1F5F9', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width, borderRadius: 5,
                      background: i === stageKeys.length - 1
                        ? 'linear-gradient(90deg,#2563EB,#1D4ED8)'
                        : 'linear-gradient(90deg,#60A5FA,#3B82F6)',
                      transition: 'width .4s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Context footer */}
          <div style={{ marginTop: 16, fontSize: 11, color: '#94A3B8' }}>
            Data from {new Date(data.from).toLocaleString('en-GB')} to {new Date(data.to).toLocaleString('en-GB')}
            {data.path_filter && <> · filtered to <code>{data.path_filter}</code></>}
          </div>
        </>
      )}
    </div>
  );
}
